import React, { useEffect, useMemo, useState } from "react";
import { apiGet, BACKEND_URL } from "@/lib/api";
import { Layers, Search, Loader2, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";

const SERII = [
  { cod: "71", nume: "71mm" },
  { cod: "83", nume: "83mm" },
  { cod: "93", nume: "93mm" },
];

export default function ProfilePrelucratePage() {
  const [items, setItems] = useState([]);
  const [tipuri, setTipuri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [tipFilter, setTipFilter] = useState("all");
  const [serieFilter, setSerieFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("activ");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [prel, tp] = await Promise.all([
          apiGet("/lemn/prelucrate"),
          apiGet("/lemn/tipuri-profile"),
        ]);
        setItems(prel.items || []);
        setTipuri((tp.items || []).filter((t) => t.activ !== false));
      } catch (e) {
        setErr(e.message || "Eroare la incarcare");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const tipNume = useMemo(() => {
    const m = {};
    tipuri.forEach((t) => (m[t.cod] = t.nume));
    return m;
  }, [tipuri]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const tip = it.tip || it.tip_profil || "";
      if (tipFilter !== "all" && tip !== tipFilter) return false;
      if (serieFilter !== "all") {
        const s = String(it.serie || "").trim();
        if (s && s !== "toate") {
          if (s !== serieFilter) return false;
        } else {
          if (!String(it.sectiune_latime || "").startsWith(serieFilter)) return false;
        }
      }
      if (statusFilter === "activ" && it.activ === false) return false;
      if (statusFilter === "inactiv" && it.activ !== false) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${it.cod || ""} ${it.nume || ""} ${it.denumire || ""} ${it.descriere || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, tipFilter, serieFilter, statusFilter, search]);

  const selCls = "h-9 px-3 rounded-md border border-slate-300 bg-white text-sm";

  return (
    <div className="space-y-4" data-testid="prelucrate-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Layers className="h-6 w-6 text-amber-600" /> Profile Prelucrate
          </h1>
          <p className="text-sm text-slate-500" data-testid="prelucrate-count">
            {filtered.length} profile afisate din {items.length}
          </p>
        </div>
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="prelucrate-search"
            placeholder="Cauta cod sau nume..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <select data-testid="filter-tip" className={selCls} value={tipFilter} onChange={(e) => setTipFilter(e.target.value)}>
          <option value="all">Toate tipurile</option>
          {tipuri.map((t) => (
            <option key={t.cod} value={t.cod}>{t.nume}</option>
          ))}
        </select>
        <select data-testid="filter-serie" className={selCls} value={serieFilter} onChange={(e) => setSerieFilter(e.target.value)}>
          <option value="all">Toate seriile</option>
          {SERII.map((s) => (
            <option key={s.cod} value={s.cod}>{s.nume}</option>
          ))}
        </select>
        <select data-testid="filter-status" className={selCls} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Toate statusurile</option>
          <option value="activ">Active</option>
          <option value="inactiv">Inactive</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Se incarca...
        </div>
      ) : err ? (
        <div className="text-center py-12 text-red-600 text-sm" data-testid="prelucrate-error">{err}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Layers className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Nu exista profile pentru filtrele selectate</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-sm min-w-[820px]" data-testid="prelucrate-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Figura</th>
                <th className="text-left p-3 font-medium text-slate-600">Cod</th>
                <th className="text-left p-3 font-medium text-slate-600">Nume</th>
                <th className="text-left p-3 font-medium text-slate-600">Tip</th>
                <th className="text-left p-3 font-medium text-slate-600">Sectiune</th>
                <th className="text-left p-3 font-medium text-slate-600">Profil Brut</th>
                <th className="text-left p-3 font-medium text-slate-600">Interior</th>
                <th className="text-left p-3 font-medium text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((it) => (
                <tr key={it.id} data-testid={`prelucrate-row-${it.cod || it.id}`} className={`hover:bg-slate-50 ${it.activ === false ? "opacity-60" : ""}`}>
                  <td className="p-3">
                    {it.imagine_url ? (
                      <img
                        src={`${BACKEND_URL}${it.imagine_url}`}
                        alt={it.cod || "profil"}
                        data-testid={`prelucrate-img-${it.cod || it.id}`}
                        className="w-12 h-12 object-contain rounded border border-slate-200 bg-white"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="w-12 h-12 bg-slate-100 rounded items-center justify-center"
                      style={{ display: it.imagine_url ? "none" : "flex" }}
                    >
                      <ImageIcon className="h-4 w-4 text-slate-300" />
                    </div>
                  </td>
                  <td className="p-3 font-mono text-xs">{it.cod || "-"}</td>
                  <td className="p-3 font-medium">{it.nume || it.denumire || "-"}</td>
                  <td className="p-3">
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                      {tipNume[it.tip || it.tip_profil] || it.tip || it.tip_profil || "-"}
                    </span>
                  </td>
                  <td className="p-3">{it.sectiune_latime || 0}x{it.sectiune_inaltime || 0} mm</td>
                  <td className="p-3">{it.profil_brut_sectiune || "-"}</td>
                  <td className="p-3">{it.interior || "-"}</td>
                  <td className="p-3">
                    {it.activ === false ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactiv</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Activ</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
